import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { base as duiBase } from "@dui/primitives/core";
import { CONFIG } from "../config.ts";
import { supabase } from "../supabase.ts";

/** What the page knows about the pending authorization, once loaded. */
type Consent = {
  readonly clientName: string;
  readonly redirectHost: string;
  readonly email: string;
};

type ConsentState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly consent: Consent }
  | { readonly status: "deciding"; readonly consent: Consent }
  | { readonly status: "gone" }
  | { readonly status: "error"; readonly message: string };

/** The host of a redirect URI, or the URI itself when it does not parse. */
const hostOf = (uri: string): string => {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
};

/**
 * The OAuth consent page Supabase Auth sends the browser to
 * (`auth.oauth_server.authorization_url_path`). `<accounts-root>` renders it
 * only once signed in (a signed-out visitor signs in in place first), so a
 * session exists. Loads the pending authorization named by the
 * `authorization_id` query param, shows who is asking, and hands the
 * decision to Supabase; the answer is a redirect back to the client. One of
 * ETO's own clients (`CONFIG.firstPartyClientIds`) is approved without
 * asking. "Use a different account" signs out here, and `<accounts-root>`
 * brings back the sign-in form on this same URL. Supabase gives a pending
 * authorization to the first account that loads it, so another account finds
 * it gone, as it does an expired one: either way the person connects again
 * from the client.
 */
@customElement("consent-page")
export class ConsentPage extends LitElement {
  static override styles = [
    duiBase,
    css`
      :host {
        display: block;
        width: 100%;
        max-width: 24rem;
      }
      .details {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        font-size: var(--text-sm);
        color: var(--text-2);
      }
      .details strong {
        color: var(--text-1);
      }
      .hint {
        color: var(--muted-foreground);
        font-size: var(--text-sm);
      }
      .error {
        color: var(--destructive);
        font-size: var(--text-sm);
      }
      /* A card pads its bottom only through its footer. */
      .footerless::part(content) {
        padding-bottom: var(--card-py-bottom);
      }
      .stack {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }
      .actions {
        display: flex;
        justify-content: end;
        gap: var(--space-2);
      }
      /* Shown as a link at rest; DUI's link underlines only on hover. */
      .switch {
        align-self: center;
      }
      .switch::part(root) {
        text-decoration-color: currentColor;
      }
    `,
  ];

  @state()
  accessor #state: ConsentState = { status: "loading" };

  #authorizationId: string | undefined;

  override connectedCallback(): void {
    super.connectedCallback();
    const id = new URLSearchParams(globalThis.location.search).get(
      "authorization_id",
    );
    this.#authorizationId = id ?? undefined;
    this.#load();
  }

  async #load(): Promise<void> {
    if (this.#authorizationId === undefined) {
      this.#state = {
        status: "error",
        message: "This page needs an authorization request to act on.",
      };
      return;
    }
    const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(
      this.#authorizationId,
    );
    if (error?.code === "oauth_authorization_not_found") {
      this.#state = { status: "gone" };
      return;
    }
    if (error != undefined || data == undefined) {
      this.#state = {
        status: "error",
        message: error?.message ?? "The authorization request was not found.",
      };
      return;
    }
    if ("redirect_url" in data) {
      // Already consented: nothing to ask.
      globalThis.location.assign(data.redirect_url);
      return;
    }
    if (CONFIG.firstPartyClientIds.includes(data.client.id)) {
      // One of ETO's own clients: approve without asking. The spinner stays up until the redirect, so no card flashes.
      await this.#send(this.#authorizationId, "approve");
      return;
    }
    this.#state = {
      status: "ready",
      consent: {
        clientName: data.client.name,
        redirectHost: hostOf(data.redirect_uri),
        email: data.user.email,
      },
    };
  }

  async #decide(action: "approve" | "deny"): Promise<void> {
    if (this.#state.status !== "ready" || this.#authorizationId === undefined) {
      return;
    }
    this.#state = { status: "deciding", consent: this.#state.consent };
    await this.#send(this.#authorizationId, action);
  }

  /** Record the decision with Supabase and follow its redirect back to the client. */
  async #send(id: string, action: "approve" | "deny"): Promise<void> {
    const { data, error } =
      action === "approve"
        ? await supabase.auth.oauth.approveAuthorization(id, {
            skipBrowserRedirect: true,
          })
        : await supabase.auth.oauth.denyAuthorization(id, {
            skipBrowserRedirect: true,
          });
    if (error != undefined || data == undefined) {
      this.#state = {
        status: "error",
        message: error?.message ?? "The decision was not recorded.",
      };
      return;
    }
    globalThis.location.assign(data.redirect_url);
  }

  #approve = (): void => {
    this.#decide("approve");
  };

  #deny = (): void => {
    this.#decide("deny");
  };

  #switchAccount = (): void => {
    void supabase.auth.signOut({ scope: "local" });
  };

  override render(): TemplateResult {
    const state = this.#state;
    if (state.status === "loading") {
      return html`<dui-spinner size="lg"></dui-spinner>`;
    }
    if (state.status === "error") {
      return html`
        <dui-card class="footerless">
          <span slot="title">Authorization failed</span>
          <p class="error">${state.message}</p>
        </dui-card>
      `;
    }
    if (state.status === "gone") {
      return html`
        <dui-card class="footerless">
          <span slot="title">Connect again</span>
          <p class="hint">
            This request has ended or belongs to another account. Go back to the
            app that sent you here and connect again.
          </p>
        </dui-card>
      `;
    }
    const { consent } = state;
    const deciding = state.status === "deciding";
    return html`
      <div class="stack">
        <dui-card>
          <span slot="title">Authorize ${consent.clientName}</span>
          <span slot="description">
            It will use ETO as <strong>${consent.email}</strong>.
          </span>
          <div class="details">
            <span>
              It can read and change your ETO workspaces as you, and stays
              signed in.
            </span>
            <span>Returns to <strong>${consent.redirectHost}</strong></span>
          </div>
          <div slot="footer" class="actions">
            <dui-button
              appearance="outline"
              ?disabled=${deciding}
              @click=${this.#deny}
            >
              Deny
            </dui-button>
            <dui-button
              variant="primary"
              ?disabled=${deciding}
              @click=${this.#approve}
            >
              ${deciding
                ? html`<dui-spinner size="sm"></dui-spinner>`
                : "Approve"}
            </dui-button>
          </div>
        </dui-card>
        <dui-button
          class="switch"
          appearance="link"
          ?disabled=${deciding}
          @click=${this.#switchAccount}
        >
          Use a different account
        </dui-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "consent-page": ConsentPage;
  }
}
